import type { AnswerPayload } from '@/domains/survey';
import type { IOversightAnswer } from './api/oversight-contracts';

/**
 * A stored answer, rendered for reading.
 *
 * The oversight detail hands over three views of the same value — the canonical payload
 * (`{"t":"money","v":1234567}`), the server's own `valueNum` / `valueText` projection, and
 * the resolved `options` — and this picks between them rather than re-deriving one. Order
 * matters: `options` first because an option code is not what a reader wants to see, then
 * the projection because it is the server's reading of the payload, and only then the
 * payload itself for the shapes the projection has no column for (dates, grid cells).
 *
 * It is not the write path's `answer-values.ts`. That module maps a **field type** to a
 * payload tag, because that is what a write is validated against; here nothing is written
 * and the tag is already in hand, so switching on the field type would only add a way for
 * the two to disagree.
 *
 * `{}` and `''` come back as an em dash rather than as an empty cell: "no answer" and "a
 * cell the renderer had nothing to say about" look identical when both are blank.
 */

export const NO_ANSWER = '—';

export function answerText(answer: IOversightAnswer): string {
  if (answer.options.length > 0) {
    return answer.options.map((option) => option.label || option.code).join(', ');
  }
  if (answer.valueText !== null && answer.valueText !== '') return answer.valueText;
  if (answer.valueNum !== null) return formatNumber(answer.valueNum);

  return payloadText(answer.value);
}

/** The payload on its own — what the orphan list and a provenance value have to show. */
export function payloadText(payload: Nullable<AnswerPayload>): string {
  if (!payload) return NO_ANSWER;

  switch (payload.t) {
    case 'text':
    case 'date':
      return payload.v === '' ? NO_ANSWER : payload.v;
    case 'int':
    case 'num':
      return formatNumber(payload.v);
    case 'pct':
      return `${formatNumber(payload.v)}%`;
    case 'money':
      return `${formatNumber(payload.v)} ${payload.cur}`;
    case 'opts':
      return payload.v.length > 0 ? payload.v.join(', ') : NO_ANSWER;
    case 'cells':
      // Grid and multi-year answers are a map of cells with no shared column order to
      // impose here — the questionnaire that would supply one is the form's job. `key: value`
      // pairs keep every cell visible, which is what an oversight reader is here for.
      return cellsText(payload.v);
    default:
      // The union is closed today; a payload tag added server-side must not render blank.
      return JSON.stringify((payload as { v?: unknown }).v ?? payload);
  }
}

function cellsText(cells: Record<string, string | number | boolean | null>): string {
  const entries = Object.entries(cells).filter(([, value]) => value !== null && value !== '');
  if (entries.length === 0) return NO_ANSWER;

  return entries
    .map(([key, value]) => `${key}: ${typeof value === 'number' ? formatNumber(value) : value}`)
    .join(' · ');
}

/** Grouped, and never rounded: an oversight screen shows the stored value, not a summary. */
export function formatNumber(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 20 });
}

/**
 * The address an answer sits at, for a column that has to distinguish two rows of the same
 * usage. Empty for the scalar case, which is most of them — the runtime's own convention.
 */
export function answerAddress(
  answer: Pick<IOversightAnswer, 'instanceId' | 'instanceKey'>
): string {
  if (answer.instanceKey === '' && answer.instanceId === null) return '';
  if (answer.instanceId === null) return answer.instanceKey;
  // The row is identified in `repeats[]` by this id; showing a prefix keeps the column
  // narrow while staying matchable against that list.
  const row = `row ${answer.instanceId.slice(0, 8)}`;
  return answer.instanceKey === '' ? row : `${row} · ${answer.instanceKey}`;
}
